// Browser-side CSV download. Mirrors /events.csv from app.py — same
// column order, same value formatting, same `pwhl_events_<event_group>.csv`
// filename pattern.
//
// Uses Blob + URL.createObjectURL + <a download> so the browser saves to
// disk without a server round-trip. Memory: each export builds the
// full CSV in a string before emitting the blob; a 10k-row export is
// ~1 MB of text, well within reason.

import type { EventRow } from "./query";

const HEADER = [
  "event_id",
  "game_id",
  "date",
  "home",
  "away",
  "period",
  "time_remaining",
  "event_type",
  "zone",
  "x",
  "y",
  "team",
  "player",
  "shot_type",
  "shot_quality",
  "is_goal",
  "xg",
  "score_diff_for",
];

function csvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const s = String(value);
  // RFC 4180-ish: quote when the cell contains comma, quote, or newline.
  // Inner quotes get doubled.
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function buildCsv(rows: EventRow[]): string {
  const lines = [HEADER.join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.event_id,
        r.game_id,
        r.date_played,
        r.home_code,
        r.away_code,
        r.period,
        r.time_remaining,
        r.event_type,
        r.zone,
        r.x_loc,
        r.y_loc,
        r.team_code,
        r.player_name,
        r.shot_type,
        r.shot_quality,
        r.is_goal,
        r.xg !== null ? r.xg.toFixed(4) : "",
        r.score_diff_for,
      ]
        .map(csvCell)
        .join(","),
    );
  }
  return lines.join("\n");
}

export function triggerDownload(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on next tick — the click+download has already kicked off.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
